/**
 * TanStack Query hooks for `braze-iterable-customer-io-integrations`.
 * Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	listConfigs,
	createConfig,
	deleteConfig,
	listEvents,
	createEvent,
	brazeIterableCustomerIoIntegrationsQueryKeys,
	type CreateConfigBody,
	type CreateEventBody,
	type CreateEventResponse,
	type IntegrationConfig,
	type ListConfigsResponse,
	type ListEventsResponse,
} from "./api";

export function useIntegrationConfigsList(): UseQueryResult<
	ListConfigsResponse,
	Error
> {
	return useQuery({
		queryKey: brazeIterableCustomerIoIntegrationsQueryKeys.list(),
		queryFn: listConfigs,
	});
}

export function useIntegrationConfigCreate(): UseMutationResult<
	IntegrationConfig,
	Error,
	CreateConfigBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createConfig,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: brazeIterableCustomerIoIntegrationsQueryKeys.all,
			});
		},
	});
}

export function useIntegrationConfigDelete(): UseMutationResult<
	{ ok: boolean; disabled: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deleteConfig,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: brazeIterableCustomerIoIntegrationsQueryKeys.all,
			});
		},
	});
}

export function useIntegrationEventsList(
	configId: string | null,
): UseQueryResult<ListEventsResponse, Error> {
	return useQuery({
		queryKey: configId
			? brazeIterableCustomerIoIntegrationsQueryKeys.events(configId)
			: ["braze-iterable-customer-io-integrations", "events", "disabled"],
		queryFn: () => listEvents(configId as string),
		enabled: Boolean(configId),
	});
}

export function useIntegrationEventCreate(
	configId: string | null,
): UseMutationResult<CreateEventResponse, Error, CreateEventBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateEventBody) => {
			if (!configId) return Promise.reject(new Error("no config selected"));
			return createEvent(configId, body);
		},
		onSuccess: () => {
			if (configId) {
				qc.invalidateQueries({
					queryKey: brazeIterableCustomerIoIntegrationsQueryKeys.events(configId),
				});
			}
		},
	});
}
